import { useMemo, useState } from "react";
import { useImmer } from "use-immer";

import { IgFilterAutocomplete } from "@intelligems/ig-theme";
import {
  ChannelType,
  ConversionStageReachedType,
  CountryEnum,
  CustomEventsFilterType,
  DeviceType,
  ExperienceCategory,
  GenericExperienceFe,
  LandingPageFilterType,
  StatSigViewType,
  UrlMatch,
  UrlParamPayloadType,
  VisitorType,
} from "@intelligems/ig-types";
import { getTinybirdActiveAnalyticsFilters } from "@src/api/analytics/utils";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { usePatchExperience } from "@src/api/experiences/patch-experience";
import type { AnalyticFiltersConfigType } from "@src/api/types/dashboards";
import { DashboardPageName } from "@src/api/types/dashboards";
import { AnalyticsFiltersDrawer } from "@src/features/analytics/analyticsFilters/AnalyticsFiltersDrawer";
import { AnalyticsViewTypeFilter } from "@src/features/analytics/analyticsFilters/AnalyticsViewTypeFilter";
import { CustomEventsFilter } from "@src/features/analytics/analyticsFilters/CustomEventsFilter";
import { DeviceTypeFilter } from "@src/features/analytics/analyticsFilters/DeviceTypeFilter";
import { UserBehaviorFilter } from "@src/features/analytics/analyticsFilters/UserBehaviorFilter";
import {
  getAllChannelTypes,
  getChannelTypeListCount,
  useDialogFilterValidation,
  useSitewideDrawerFilterValidation,
} from "@src/features/analytics/analyticsFilters/utils";
import { VisitorTypeFilter } from "@src/features/analytics/analyticsFilters/VisitorTypeFilter";
import { useDispatch, useSelector } from "@src/store";
import { getAnalyticsProducts } from "@src/store/slices/analyticsProducts";
import { setDashboardFilter } from "@src/store/slices/dashboardFilters";
import { closeDrawer } from "@src/store/slices/drawers";
import { getCustomMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperience } from "@src/store/slices/experiences";
import {
  selectedSitewideDashboardFilters,
  setSitewideDashboardFilter,
} from "@src/store/slices/sitewideDashboardFilters";
import { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";
import { ISitewideDashboardFilters } from "@src/store/types/sitewideDashboardFilters";
import { mpClickAnalyticsFilterSaveButton } from "@src/utils/mixpanel";

import { ChannelChecklist } from "../../../components/AudienceFilters/Channel";

import { LandingPageFilter } from "./LandingPageFilter";
import { UrlParamFilter } from "./UrlParamFilter";
import { ZScoreFilter } from "./ZScoreFilter";

interface AnalyticsFilterDrawerWrapperProps {
  pageName?: DashboardPageName;
  openFilters: boolean;
  allCountryCodesData?: string[];
}

export const SitewideAnalyticsFilterDrawerWrapper = ({
  pageName,
  openFilters,
  allCountryCodesData,
}: AnalyticsFilterDrawerWrapperProps) => {
  const dispatch = useDispatch();
  const sitewideDashboardFilters = useSelector(
    (state) => state.sitewideDashboardFilters
  );

  const maxZScoreConfig = useConfiguration("max_z_score");
  const isHighVolumeAnalytics = useConfiguration("is_high_volume_analytics");

  const [dialogFilters, setDialogFilters] = useImmer<ISitewideDashboardFilters>(
    {
      ...sitewideDashboardFilters,
      landingPage: sitewideDashboardFilters.landingPage,
      isZScoreEnabled: sitewideDashboardFilters.isZScoreEnabled ?? true,
      maxZScore: sitewideDashboardFilters.maxZScore ?? maxZScoreConfig!.value,
      countryCodes: sitewideDashboardFilters.countryCodes || [],
      startTime: null,
      endTime: null,

      deviceType: sitewideDashboardFilters.deviceType || "any",
      visitorType: sitewideDashboardFilters.visitorType || "any",
      channels:
        sitewideDashboardFilters.channels.length === 0
          ? getAllChannelTypes()
          : sitewideDashboardFilters.channels,
    }
  );

  const [isTestProductsDefaultChange, setIsTestProductsDefaultChange] =
    useState<boolean>(false);
  const validationErrors = useSitewideDrawerFilterValidation(dialogFilters);

  const onStringFilterChange = (
    newValue: DeviceType | VisitorType | ChannelType,
    filterKey: "deviceType" | "channelType" | "visitorType"
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = newValue;
      return draft;
    });
  };

  const onBooleanFilterChange = (
    checked: boolean,
    filterKey: "testOrdersOnly" | "isZScoreEnabled"
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = checked;
      return draft;
    });
  };

  const onStringArrFilterChange = (
    newValues: string[],
    filterKey: "customEvents" | "countryCodes"
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = newValues;
      return draft;
    });
  };

  const onChannelClick = (channelType: ChannelType | null) => {
    const updatedChannels = dialogFilters.channels.filter(
      (channel) => !!channel
    );
    if (!channelType) {
      // handles all channels button
      setDialogFilters((draft) => {
        draft.channels =
          draft.channels.length === getChannelTypeListCount()
            ? []
            : getAllChannelTypes();
        return draft;
      });
    } else if (updatedChannels.includes(channelType)) {
      // handles removing channel when un selected
      setDialogFilters((draft) => {
        draft.channels = draft.channels.filter(
          (channel) => channel !== channelType
        );
        return draft;
      });
    } else {
      // handles adding unselected channels when selected
      setDialogFilters((draft) => {
        draft.channels = [...(updatedChannels || []), channelType];
      });
    }
  };

  const onLandingPageChange = (
    value: string | null,
    filter: LandingPageFilterType | null
  ) => {
    setDialogFilters((draft) => {
      draft.landingPage = {
        ...draft.landingPage,
        landingPageUrlPath: value,
        landingPageUrlPathFilterType: filter,
      };
    });
  };

  const filterButtons: AnalyticFiltersConfigType[] = useMemo(() => {
    return [
      {
        label: "Device Type",
        settings: [
          {
            key: "deviceType",
            description: "Filter by device type",
            component: (
              <DeviceTypeFilter
                onStringFilterChange={onStringFilterChange}
                deviceType={dialogFilters.deviceType}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
      },
      {
        label: "Visitor Type",
        settings: [
          {
            key: "visitorType",
            description: "Filter by visitor type",
            component: (
              <VisitorTypeFilter
                onStringFilterChange={onStringFilterChange}
                visitorType={dialogFilters.visitorType}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "ordersMatchedToSession",
      },
      {
        label: "Source Channels & Sites",
        settings: [
          {
            key: "channelType",
            description:
              "Filter by one more more channel and traffic source type",
            component: (
              <ChannelChecklist
                onClick={onChannelClick}
                channels={dialogFilters.channels}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "ordersMatchedToSession",
      },
      {
        label: "Z-Score",
        settings: [
          {
            key: "isZScoreEnabled",
            description: "Max Z-Score",
            component: (
              <ZScoreFilter
                isZScoreEnabled={dialogFilters.isZScoreEnabled}
                setDialogFilters={setDialogFilters}
                maxZScore={dialogFilters.maxZScore}
                onBooleanFilterChange={onBooleanFilterChange}
              />
            ),
          },
        ],
        isSwitch: true,
        showOnPage: "all",
      },
      {
        label: "Countries",
        settings: [
          {
            key: "countryCodes",
            description:
              "Filter to visitors who were in one of these countries",
            component: (
              <IgFilterAutocomplete
                selectedValues={dialogFilters.countryCodes}
                onChange={onStringArrFilterChange}
                filterKey={"countryCodes"}
                allDataOptions={allCountryCodesData!}
                placeholder={"Countries"}
                disableCloseOnSelect={true}
                getOptionDisabled={(option) => option === null}
                getOptionLabel={(option) =>
                  `${CountryEnum[option] ?? "Unknown"} ${option ? `(${option})` : ""}`
                }
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "ordersMatchedToSession",
        config: allCountryCodesData && allCountryCodesData?.length > 0,
      },
      {
        label: "Landing Page",
        isSwitch: false,
        showOnPage: "ordersMatchedToSession",
        settings: [
          {
            key: "landingPage",
            description: "Filter to visitors by their landing page.",
            component: (
              <LandingPageFilter
                value={dialogFilters.landingPage?.landingPageUrlPath || null}
                filter={
                  dialogFilters.landingPage?.landingPageUrlPathFilterType ||
                  null
                }
                onChange={onLandingPageChange}
              />
            ),
          },
        ],
      },
    ];
  }, [isHighVolumeAnalytics?.value, dialogFilters, allCountryCodesData]);

  const onFiltersClearAll = () => {
    dispatch(
      setSitewideDashboardFilter({
        ...sitewideDashboardFilters,
        isZScoreEnabled: true,
        deviceType: null,
        visitorType: null,
        channels: [],
        maxZScore: maxZScoreConfig!.value,
        countryCodes: [],
        landingPage: {
          landingPageUrlPath: null,
          landingPageUrlPathFilterType: null,
        },
      })
    );

    setDialogFilters((draft) => {
      draft.isZScoreEnabled = true;
      draft.maxZScore = maxZScoreConfig!.value;
      draft.deviceType = null;
      draft.channels = [];
      draft.visitorType = null;
      draft.countryCodes = [];
      draft.landingPage = {
        landingPageUrlPath: null,
        landingPageUrlPathFilterType: null,
      };
      return draft;
    });

    dispatch(closeDrawer("analyticsFilters"));
  };

  const onFiltersSave = () => {
    const { startTime, endTime, ...rest } = [dialogFilters].map((filters) => {
      return {
        ...filters,
        channels:
          filters.channels.length === getChannelTypeListCount()
            ? []
            : filters.channels,
      };
    })[0];
    const getTbActiveFilters = getTinybirdActiveAnalyticsFilters(
      {
        ...sitewideDashboardFilters,
        ...rest,
      },
      maxZScoreConfig!.value
    );
    // if (Object.keys(getTbActiveFilters).length && experience) {
    //   mpClickAnalyticsFilterSaveButton(
    //     experience.id,
    //     getTbActiveFilters,
    //     pageName || "overview"
    //   );
    // }
    dispatch(
      setSitewideDashboardFilter({ ...sitewideDashboardFilters, ...rest })
    );
    dispatch(closeDrawer("analyticsFilters"));
  };

  return (
    <AnalyticsFiltersDrawer
      onFilterSave={onFiltersSave}
      openFilters={openFilters}
      onFiltersClearAll={onFiltersClearAll}
      filterButtons={filterButtons.filter(
        (button) =>
          // if showOnPage is not defined, default to all treatments
          !button.showOnPage ||
          // if selected ordersMatchedToSession equals showOnPage then show
          dialogFilters.ordersMatchedToSession === button.showOnPage ||
          // if selected ordersMatchedToSession is all, always show
          button.showOnPage === "all"
      )}
      isSaveLoading={false}
      pageName={pageName}
      isSaveDisabled={validationErrors[0] ?? { isDisabled: false, reason: "" }}
    />
  );
};
